import React, { useMemo, useState } from 'react';
import { formatPos } from './rankings';
import { addDays, trendMarkers, kindInfo, summaryLine } from '../timeline/timeline';

/*
 * Site trend with timeline flags (like GA annotations): one series (clicks,
 * impressions or average position; one y-axis), and a dated flag for every
 * change or note in the window so a ranking move can be tied to what was done.
 * Changes are filled flags, notes hollow ones, so the kind never rides on
 * colour alone. Hover a day for its value, a flag for its entries; the list
 * under the chart repeats the flags as text.
 */

const LINE = '#2a78d6';
const W = 960;
const H = 220;
const PAD = { l: 44, r: 12, t: 26, b: 24 };
const METRICS = [
  { key: 'clicks', label: 'Clicks' },
  { key: 'impressions', label: 'Impressions' },
  { key: 'position', label: 'Avg position', invert: true },
];

const fmt = (metric, v) => (v == null ? '—' : metric === 'position' ? formatPos(v) : Number(v).toLocaleString());
const short = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });

export default function AnnotatedTrend({ trend, start, bucket = 1, entries }) {
  const [metric, setMetric] = useState('clicks');
  const [hover, setHover] = useState(null);
  const m = METRICS.find((x) => x.key === metric);
  const values = (trend && trend[metric]) || [];
  const n = values.length;
  const markers = useMemo(() => trendMarkers(entries, start, bucket, n), [entries, start, bucket, n]);
  if (n < 2) return null;

  const pts = values.map((v, i) => [i, v]).filter(([, v]) => v != null);
  const vs = pts.map(([, v]) => v);
  let lo = m.invert ? Math.max(1, Math.min(...vs) - 0.5) : 0;
  let hi = m.invert ? Math.max(...vs) + 0.5 : Math.max(1, ...vs);
  if (hi === lo) { hi += 1; lo = Math.max(0, lo - 1); }
  const iw = W - PAD.l - PAD.r;
  const ih = H - PAD.t - PAD.b;
  const x = (i) => PAD.l + (i / (n - 1)) * iw;
  const y = (v) => {
    const t = (v - lo) / (hi - lo);
    return PAD.t + (m.invert ? t : 1 - t) * ih;
  };
  const ticks = [lo, (lo + hi) / 2, hi];
  const dateAt = (i) => addDays(start, i * bucket);
  const line = pts.map(([i, v]) => `${x(i)},${y(v)}`).join(' ');
  const step = iw / (n - 1);
  const hx = hover ? x(hover.type === 'flag' ? hover.mk.index : hover.i) : 0;

  return (
    <div className="annotated-trend">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 8 }}>
        <div className="chip-group" role="group" aria-label="Metric">
          {METRICS.map((o) => (
            <button key={o.key} type="button" className={`chip${metric === o.key ? ' active' : ''}`} aria-pressed={metric === o.key}
                    onClick={() => setMetric(o.key)}>{o.label}</button>
          ))}
        </div>
        <span className="muted small">
          <svg width="10" height="12" aria-hidden="true" style={{ verticalAlign: 'middle' }}><path d="M1 0v12M1 1h8l-2 3 2 3H1z" fill="var(--color-text)" stroke="var(--color-text)" /></svg> change
          {'  '}
          <svg width="10" height="12" aria-hidden="true" style={{ verticalAlign: 'middle', marginLeft: 8 }}><path d="M1 0v12M1 1h8l-2 3 2 3H1z" fill="var(--color-surface)" stroke="var(--color-text-muted, #6b7280)" /></svg> note
          {' '}(from the site&apos;s notes &amp; follow-ups)
        </span>
      </div>
      <div style={{ position: 'relative' }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" style={{ display: 'block' }}
             aria-label={`${m.label} per ${bucket === 1 ? 'day' : 'week'} from ${start}, with ${markers.length} timeline flag${markers.length === 1 ? '' : 's'}`}
             onMouseLeave={() => setHover(null)}>
          {ticks.map((t, i) => (
            <g key={i}>
              <line x1={PAD.l} x2={W - PAD.r} y1={y(t)} y2={y(t)} stroke="var(--color-border)" strokeWidth="1" />
              <text x={PAD.l - 6} y={y(t) + 4} textAnchor="end" fontSize="11" fill="var(--color-text-muted, #6b7280)">{fmt(metric, Math.round(t * 10) / 10)}</text>
            </g>
          ))}
          {[0, Math.floor((n - 1) / 2), n - 1].map((i) => (
            <text key={i} x={x(i)} y={H - 6} fontSize="11" textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}
                  fill="var(--color-text-muted, #6b7280)">{short(dateAt(i))}</text>
          ))}
          {markers.map((mk) => (
            <line key={`l${mk.index}`} x1={x(mk.index)} x2={x(mk.index)} y1={PAD.t - 14} y2={PAD.t + ih}
                  stroke="var(--color-text-muted, #6b7280)" strokeDasharray="3 3" strokeWidth="1" />
          ))}
          <polyline fill="none" stroke={LINE} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" points={line} />
          {hover && hover.type === 'day' && values[hover.i] != null && (
            <>
              <line x1={x(hover.i)} x2={x(hover.i)} y1={PAD.t} y2={PAD.t + ih} stroke="var(--color-border)" />
              <circle cx={x(hover.i)} cy={y(values[hover.i])} r="4" fill={LINE} stroke="var(--color-surface)" strokeWidth="2" />
            </>
          )}
          {values.map((v, i) => (
            <rect key={`h${i}`} x={x(i) - step / 2} y={PAD.t} width={step} height={ih} fill="transparent"
                  onMouseEnter={() => setHover({ type: 'day', i })} />
          ))}
          {markers.map((mk, k) => {
            const cx = x(mk.index);
            const change = mk.kind === 'change';
            return (
              <g key={`f${mk.index}`} style={{ cursor: 'pointer' }} onMouseEnter={() => setHover({ type: 'flag', mk })}
                 onFocus={() => setHover({ type: 'flag', mk })} tabIndex={0} aria-label={`${short(mk.date)}: ${mk.entries.map((e) => summaryLine(e.text)).join('; ')}`}>
                <rect x={cx - 6} y={PAD.t - 24} width="22" height="22" fill="transparent" />
                <path d={`M${cx} ${PAD.t - 22}h12l-3 4 3 4h-12z`} fill={change ? 'var(--color-text)' : 'var(--color-surface)'}
                      stroke={change ? 'var(--color-text)' : 'var(--color-text-muted, #6b7280)'} strokeWidth="1.5" strokeLinejoin="round" />
                <text x={cx + 18} y={PAD.t - 13} fontSize="10" fill="var(--color-text-muted, #6b7280)">{k + 1}</text>
              </g>
            );
          })}
        </svg>
        {hover && (
          <div className="small" role="status" style={{
            position: 'absolute', left: `${(hx / W) * 100}%`, top: `${((PAD.t + 4) / H) * 100}%`,
            transform: `translateX(${hx > W * 0.6 ? 'calc(-100% - 8px)' : '8px'})`, background: 'var(--color-text)', color: 'var(--color-surface, #fff)',
            padding: '6px 8px', borderRadius: 4, pointerEvents: 'none', width: 'max-content', maxWidth: 'min(340px, 70vw)', zIndex: 2,
          }}>
            {hover.type === 'day' ? (
              <>{short(dateAt(hover.i))}{bucket > 1 ? ` (week)` : ''}: <b>{fmt(metric, values[hover.i])}</b> {m.label.toLowerCase()}</>
            ) : (
              <>
                <div style={{ fontWeight: 600 }}>{short(hover.mk.date)}{bucket > 1 ? ' (week of)' : ''}</div>
                {hover.mk.entries.map((e) => (
                  <div key={e.id}>{kindInfo(e.kind).label}{e.scope === 'page' ? ` /${e.page}` : ''}: {summaryLine(e.text)}</div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
      {markers.length > 0 && (
        <ol className="small muted" style={{ margin: '8px 0 0', paddingLeft: 20 }}>
          {markers.map((mk) => mk.entries.map((e, j) => (
            <li key={e.id} value={j === 0 ? markers.indexOf(mk) + 1 : undefined} style={j > 0 ? { listStyle: 'none' } : undefined}>
              {e.date} · {kindInfo(e.kind).label}{e.author?.type === 'agent' ? ' (agent)' : ''}{e.scope === 'page' ? ` · /${e.page}` : ''}: {summaryLine(e.text, 140)}
            </li>
          )))}
        </ol>
      )}
    </div>
  );
}
